/**
 * Cross-tab auth event channel.
 *
 * Extracted from authStore.ts (review follow-up #1064, P2-1) so that BOTH
 * producers can reach the same channel:
 *
 *   - stores/authStore.ts  -- login, silent refresh, explicit logout
 *   - lib/api.ts           -- the 401 forced-logout path
 *
 * api.ts cannot import authStore (authStore imports api.ts for
 * __authInternals, so that would be a cycle). A standalone module singleton is
 * the only place both can share.
 *
 * Listeners subscribe with addEventListener rather than assigning `onmessage`,
 * because more than one module now touches this channel and a second
 * `onmessage =` would silently clobber the first.
 *
 * Note: BroadcastChannel never delivers a message back to the context that
 * posted it. Every producer must therefore apply the effect locally as well as
 * broadcasting it — the broadcast is only for the OTHER tabs.
 */

import { jwtDecode } from 'jwt-decode'

const BROADCAST_CHANNEL_NAME = 'buildmaster-auth-sync'

export type AuthBroadcastEvent =
  | {
      type: 'TOKENS_UPDATED'
      accessToken: string
      refreshToken: string | null
      /**
       * The `sub` claim of `accessToken` (PROD-SESSION-HARD-NAV-01 / F1).
       *
       * #1064 P2-1 had the receiving tab drop its permission set on EVERY
       * TOKENS_UPDATED, because the message carried no identity and the tab
       * could not tell a silent rotation for the same user from a different
       * user logging in next door. Now that a rotation is broadcast on every
       * interceptor-side refresh too, that blanket drop would flash the
       * sidebar skeleton in every other tab on every rotation -- which is the
       * flicker tracked as TOKEN-REFRESH-FLICKER-01.
       *
       * Carrying the subject lets the receiver drop the set only when the
       * identity actually changed. Optional so a tab still running the older
       * bundle (which omits it) is read as "unknown identity" and keeps the
       * old, safe behaviour of dropping the set.
       */
      sub?: string | null
    }
  | { type: 'LOGGED_OUT' }

/**
 * The `sub` claim of a JWT, or null when the token is absent/undecodable.
 *
 * Lives here rather than in either producer so lib/api.ts and
 * stores/authStore.ts stamp the broadcast the same way (api.ts cannot import
 * the store -- see the module header).
 */
export function tokenSubject(token: string | null | undefined): string | null {
  if (!token) return null
  try {
    const payload = jwtDecode<{ sub?: unknown }>(token)
    return typeof payload.sub === 'string' ? payload.sub : null
  } catch {
    return null
  }
}

let channel: BroadcastChannel | null = null
let resolved = false

/** The shared channel, or null where BroadcastChannel is unavailable. */
export function getAuthChannel(): BroadcastChannel | null {
  if (resolved) return channel
  resolved = true
  try {
    if (typeof BroadcastChannel !== 'undefined') {
      channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME)
    }
  } catch {
    channel = null
  }
  return channel
}

/** Post an auth event to the other tabs. Never throws. */
export function broadcastAuthEvent(event: AuthBroadcastEvent): void {
  const ch = getAuthChannel()
  if (!ch) return
  try {
    ch.postMessage(event)
  } catch {
    /* a closed or unavailable channel must never break the caller */
  }
}

/** Subscribe to auth events from other tabs. Returns an unsubscribe fn. */
export function subscribeAuthEvents(
  handler: (event: AuthBroadcastEvent) => void,
): () => void {
  const ch = getAuthChannel()
  if (!ch) return () => undefined
  const listener = (ev: MessageEvent<AuthBroadcastEvent>) => {
    if (ev.data) handler(ev.data)
  }
  ch.addEventListener('message', listener)
  return () => ch.removeEventListener('message', listener)
}

/**
 * Cross-tab token sync for browsers WITHOUT BroadcastChannel (P2-3).
 *
 * `subscribeAuthEvents` is a no-op where the channel is unavailable, so those
 * browsers heard nothing when another tab rotated: storage was right, the store
 * was stale, and only a full reload fixed it. `storage` events are the fallback
 * every browser has -- they fire in OTHER tabs only, never in the writer, which
 * is exactly the semantics wanted here.
 *
 * THE EVENT IS A TRIGGER, NOT A PAYLOAD (round-2 review P2).
 *
 * The first version handed the subscriber `ev.newValue` as the access token and
 * read its partner from storage. That pairs a value captured WHEN THE EVENT WAS
 * QUEUED with one read WHEN IT WAS DELIVERED -- two different instants -- and
 * `storage` events are delivered asynchronously, so under two tabs rotating in
 * sequence a late gen-1 event arrived carrying gen-1's access token beside
 * gen-2's refresh token. Any subscriber that wrote that pair back downgraded a
 * NEWER token to an older one, and woke every other tab to do the same.
 *
 * So both halves are now read from storage at delivery time. Storage is the
 * shared truth on this origin and the writer has already updated it; the event
 * only says "go and look". A late or duplicate event therefore cannot carry a
 * stale token, because it carries no token at all -- at worst it delivers the
 * current pair twice, and the subscriber's own identity check absorbs that.
 *
 * NO REBROADCAST, AND NO WRITE. The handler feeds local sinks and stops. A
 * handler that answered a `storage` event by writing storage would have every
 * tab wake every other tab; the writer is the only publisher, everyone else is
 * a reader.
 *
 * Inert where BroadcastChannel exists, so a browser never gets both paths for
 * the same event.
 */
export function subscribeTokenStorageSync(
  handler: (tokens: { accessToken: string; refreshToken: string | null }) => void,
  keys: { access: string; refresh: string },
): () => void {
  if (getAuthChannel()) return () => undefined
  if (typeof window === 'undefined' || !window.addEventListener) {
    return () => undefined
  }

  /** The pair as it stands NOW, or null if there is no live access token. */
  const currentPair = (): { accessToken: string; refreshToken: string | null } | null => {
    try {
      const accessToken = window.localStorage.getItem(keys.access)
      if (!accessToken) return null
      return { accessToken, refreshToken: window.localStorage.getItem(keys.refresh) }
    } catch {
      // A blocked or unavailable localStorage cannot be reconciled against.
      return null
    }
  }

  const listener = (ev: StorageEvent) => {
    // Only our own keys, and only a WRITE (a clear sets newValue to null and is
    // the logout path's business, not this one's). `ev.newValue` is used HERE
    // ONLY, to tell a write from a clear -- never as the token itself.
    if (ev.key !== keys.access || !ev.newValue) return
    // Re-read rather than trust the event. A clear that landed between the event
    // being queued and delivered leaves nothing to adopt, and inventing a
    // session out of a stale event value is how a logout gets undone.
    const pair = currentPair()
    if (!pair) return
    try {
      handler(pair)
    } catch {
      /* a throwing sink is its own problem */
    }
  }

  window.addEventListener('storage', listener)
  return () => window.removeEventListener('storage', listener)
}

/** Test-only: drop the memoized channel so a fresh one is created. */
export function __resetAuthChannelForTests(): void {
  try {
    channel?.close()
  } catch {
    /* noop */
  }
  channel = null
  resolved = false
}
